import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import {
  Coins,
  Gift,
  Users,
  Sparkles,
  ExternalLink,
  Wallet,
  AlertCircle,
  ShoppingCart,
  Gamepad2,
  Trophy,
  Zap,
  TrendingUp,
  ArrowRight,
} from "lucide-react";

const tokenFeatures = [
  {
    icon: Gift,
    title: "Monthly Rewards",
    description: "Annual members receive $10 worth of PayCoin every month.",
  },
  {
    icon: Users,
    title: "Community Incentives",
    description: "Earn tokens through active community participation.",
  },
  {
    icon: Sparkles,
    title: "Ecosystem Benefits",
    description: "Access exclusive features and early access programs.",
  },
  {
    icon: Wallet,
    title: "Future Utilities",
    description: "More utilities coming as the ecosystem grows.",
  },
];

const games = [
  {
    icon: Gamepad2,
    title: "Lucky 7",
    description: "Roll the dice and test your luck. Win PAY tokens on winning rolls.",
    status: "Live",
    link: "/play",
  },
  {
    icon: Trophy,
    title: "Prediction Games",
    description: "Predict market movements and earn rewards for correct predictions.",
    status: "Coming Soon",
  },
  {
    icon: Zap,
    title: "Daily Challenges",
    description: "Complete daily tasks and challenges to earn bonus PAY tokens.",
    status: "Coming Soon",
  },
];

export default function PayCoin() {
  return (
    <div className="min-h-screen bg-background">
      <NavBar />

      {/* Hero */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 via-transparent to-primary/5" />
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[150px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 mb-6">
              <Coins className="w-4 h-4 text-secondary" />
              <span className="text-sm text-secondary font-medium">Utility Token</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black mb-6">
              Pay<span className="text-secondary neon-text-secondary">Coin</span> (PAY)
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground mb-8">
              The utility token powering the PassivePay ecosystem.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ========== SECTION 1: About PayCoin ========== */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              About <span className="text-secondary">PayCoin</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              PayCoin is built on the <span className="text-primary font-medium">Solana blockchain</span> and 
              is designed to reward long-term community participation. It's a utility-focused token that 
              provides real benefits within the PassivePay ecosystem.
            </p>
          </motion.div>

          {/* Token Features */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {tokenFeatures.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card rounded-xl p-6 hover:border-secondary/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-secondary/20 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-secondary" />
                  </div>
                  <h3 className="font-heading font-bold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Transparency Info */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card rounded-xl p-6 text-center"
            >
              <div className="text-sm text-muted-foreground mb-2">Blockchain</div>
              <div className="text-2xl font-heading font-bold text-primary">Solana</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card rounded-xl p-6 text-center"
            >
              <div className="text-sm text-muted-foreground mb-2">Focus</div>
              <div className="text-2xl font-heading font-bold text-secondary">Utility</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-card rounded-xl p-6 text-center"
            >
              <div className="text-sm text-muted-foreground mb-2">Appreciation</div>
              <div className="text-2xl font-heading font-bold text-accent">Utility-Driven</div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-start gap-4 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20 max-w-4xl mx-auto"
          >
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              PayCoin is a utility token. We make no promises about price appreciation. 
              The value comes from its utility within the PassivePay ecosystem.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ========== SECTION 2: Buy PayCoin ========== */}
      <section className="py-20 relative bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <ShoppingCart className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Now Listed</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              Buy <span className="text-primary">PayCoin</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              PayCoin is now listed on DexScreener! You can buy, sell, and track PAY token directly.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 md:p-12 max-w-3xl mx-auto border-primary/30"
          >
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-10 h-10 text-primary" />
              </div>
              
              <h3 className="text-2xl font-heading font-bold mb-4">
                Trade on <span className="text-primary">DexScreener</span>
              </h3>
              
              <p className="text-muted-foreground mb-8">
                View real-time charts, liquidity, trading volume, and buy PAY tokens directly through DexScreener's trusted platform.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://dexscreener.com/solana/8uA1NfURM6kfBdP2ZyJYdSppG8jmu3ZjfNK7wyzjpv1" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button className="neon-glow font-heading w-full sm:w-auto">
                    <ExternalLink className="w-5 h-5 mr-2" />
                    View on DexScreener
                  </Button>
                </a>
                <Link to="/contact">
                  <Button variant="outline" className="border-primary/50 text-primary w-full sm:w-auto">
                    Need Help Buying?
                  </Button>
                </Link>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Network</div>
                    <div className="font-heading font-bold text-primary">Solana</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Symbol</div>
                    <div className="font-heading font-bold text-secondary">PAY</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Type</div>
                    <div className="font-heading font-bold text-accent">SPL Token</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Status</div>
                    <div className="font-heading font-bold text-green-400">Live</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== SECTION 3: Play & Earn PayCoin ========== */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 mb-6">
              <Gamepad2 className="w-4 h-4 text-accent" />
              <span className="text-sm text-accent font-medium">Earn While Playing</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              Play & Earn <span className="text-accent">PayCoin</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Have fun and earn PAY tokens through our exciting games and challenges. 
              More games and earning opportunities coming soon!
            </p>
          </motion.div>

          {/* Games Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {games.map((game, i) => {
              const Icon = game.icon;
              const isLive = game.status === "Live";
              
              const CardContent = (
                <>
                  <div className="absolute top-4 right-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      isLive 
                        ? "bg-green-500/20 text-green-400 border border-green-500/30" 
                        : "bg-accent/20 text-accent"
                    }`}>
                      {game.status}
                    </span>
                  </div>
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${
                    isLive ? "bg-green-500/20" : "bg-accent/20"
                  }`}>
                    <Icon className={`w-7 h-7 ${isLive ? "text-green-400" : "text-accent"}`} />
                  </div>
                  <h3 className="text-xl font-heading font-bold mb-2">{game.title}</h3>
                  <p className="text-muted-foreground mb-4">{game.description}</p>
                  {isLive && (
                    <div className="flex items-center text-green-400 font-heading font-bold text-sm">
                      Play Now <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  )}
                </>
              );
              
              return (
                <motion.div
                  key={game.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  {isLive && game.link ? (
                    <Link 
                      to={game.link}
                      className={`glass-card rounded-xl p-6 transition-colors relative overflow-hidden block ${
                        isLive 
                          ? "border-green-500/30 hover:border-green-500/50 cursor-pointer" 
                          : "hover:border-accent/30"
                      }`}
                    >
                      {CardContent}
                    </Link>
                  ) : (
                    <div className="glass-card rounded-xl p-6 hover:border-accent/30 transition-colors relative overflow-hidden">
                      {CardContent}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* CTA Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 md:p-12 border-accent/30 max-w-3xl mx-auto text-center"
          >
            <Trophy className="w-16 h-16 text-accent mx-auto mb-6" />
            <h3 className="text-2xl font-heading font-bold mb-4">
              Stay <span className="text-accent">Tuned</span>
            </h3>
            <p className="text-muted-foreground mb-6">
              We're building exciting games and earning opportunities. Join our community 
              to be the first to know when new games launch!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://t.me/passivepaycoin" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="bg-accent hover:bg-accent/90 text-white font-heading w-full sm:w-auto">
                  Join Telegram Community
                </Button>
              </a>
              <Link to="/register">
                <Button variant="outline" className="border-accent/50 text-accent w-full sm:w-auto">
                  Create Account
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
